import { SxProps, Theme } from "@mui/material";
import centeredPage from "layouts/CenteredAuthenticated/CenteredPage.tokens";
import insightsTokens, {
  insightsResponsiveTokens,
  PHONE_SM_QUERY,
} from "./Insights.tokens";
import { reportsChipTabTokens } from "screens/Reports/Reports.tokens";

const { header, tabs, cardHeading } = insightsTokens;
const { phone, tablet } = insightsResponsiveTokens;
const chip = reportsChipTabTokens;

/**
 * Hides the chip rail's scrollbar the way the reference does (`.chips::
 * -webkit-scrollbar{height:0}`) while the rail itself still scrolls.
 */
const hiddenScrollbar = {
  scrollbarWidth: "none" as const,
  msOverflowStyle: "none" as const,
  "&::-webkit-scrollbar": { display: "none", height: 0 },
};

const insightsStyles = {
  /**
   * Inner padding only — the centred measure still comes from
   * `CenteredAuthenticatedLayout`.
   *
   * Below `md` the reference runs the page edge to edge on its own gutters
   * (14/16px on a phone, 26px on the tablet), so that wrapper's gutter and top
   * margin are pulled back off first and the reference values applied on top.
   * From `md` up this is inert — the desktop screen spaces itself with the
   * `Stack`s inside.
   */
  page: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: phone.gap, sm: tablet.gap, md: 0 },
    mt: { xs: "-16px", sm: "-32px", md: 0 },
    mx: { xs: "-16px", sm: "-24px", md: 0 },
    pt: {
      xs: phone.paddingTop.std,
      sm: tablet.paddingTop,
      md: centeredPage.paddingTop,
    },
    px: {
      xs: phone.paddingX.std,
      sm: tablet.paddingX,
      md: centeredPage.paddingX,
    },
    pb: {
      xs: phone.paddingBottom.std,
      sm: tablet.paddingBottom,
      md: centeredPage.paddingBottom,
    },
    // No `width: 100%` here: with the negative gutters above it resolves
    // against the container's *content* box and pushes the page 4px past the
    // viewport. Letting the width stay `auto` is what keeps it flush.
    minWidth: 0,
    [PHONE_SM_QUERY]: {
      paddingTop: phone.paddingTop.sm,
      paddingLeft: phone.paddingX.sm,
      paddingRight: phone.paddingX.sm,
      paddingBottom: phone.paddingBottom.sm,
    },
  } as SxProps<Theme>,

  /** Title block and the Insights ▸ Reports switch. */
  header: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "flex-start" },
    justifyContent: "space-between",
    gap: { xs: phone.gap, sm: tablet.headerGap, md: 0 },
    [PHONE_SM_QUERY]: { gap: phone.gap },
  } as SxProps<Theme>,

  headerText: {
    minWidth: 0,
  } as SxProps<Theme>,

  /** The phone reference carries the screen name in the app bar, not here. */
  title: {
    display: { xs: "none", sm: "block" },
    m: 0,
    color: header.titleColor,
    fontSize: { sm: tablet.titleFontSize, md: header.titleFontSize },
    fontWeight: header.titleFontWeight,
    letterSpacing: {
      sm: tablet.titleLetterSpacing,
      md: header.titleLetterSpacing,
    },
    lineHeight: 1.2,
  } as SxProps<Theme>,

  subtitle: {
    m: 0,
    mt: {
      xs: 0,
      sm: tablet.subtitleMarginTop,
      md: header.subtitleMarginTop,
    },
    color: header.subtitleColor,
    fontSize: {
      xs: phone.subtitleFontSize.std,
      sm: tablet.subtitleFontSize,
      md: header.subtitleFontSize,
    },
    lineHeight: { xs: phone.subtitleLineHeight, sm: 1.4 },
    [PHONE_SM_QUERY]: { fontSize: phone.subtitleFontSize.sm },
  } as SxProps<Theme>,

  /**
   * The two views. A phone turns them into a chip rail that scrolls out to the
   * page gutters; the tablet and desktop keep the underline row.
   */
  tabBar: {
    display: "flex",
    alignItems: "center",
    flexWrap: { xs: "nowrap", md: "wrap" },
    gap: { xs: phone.tabsGap.std, sm: tablet.tabsGap, md: tabs.gap },
    // The desktop screen used to space these with two nested `Stack`s; the page
    // is one flex column now, so the gaps they contributed are restated here
    // (3.5 × 8px above, `tabs.marginBottom` below) and are inert below `md`.
    overflowX: { xs: "auto", md: "visible" },
    borderBottom: { xs: "none", sm: `1.5px solid ${tabs.borderColor}` },
    ...hiddenScrollbar,
    [PHONE_SM_QUERY]: { gap: phone.tabsGap.sm },
  } as SxProps<Theme>,

  /** Wrapper `ScrollHintRail` positions its chevrons against. */
  tabBarRail: {
    mt: { xs: 0, md: `${tabs.marginTopUnits * 8}px` },
    mb: { xs: 0, md: tabs.marginBottom },
  } as SxProps<Theme>,

  tab: (active: boolean) =>
    ({
      flexShrink: 0,
      minWidth: 0,
      minHeight: 0,
      whiteSpace: "nowrap",
      p: { xs: chip.padding, sm: 0 },
      pb: { sm: tablet.tabPaddingBottom, md: tabs.paddingBottom },
      // The tablet reference pads each label 2px so its 24px gap reads as 28.
      px: { sm: tablet.tabPaddingX, md: 0 },
      mb: { xs: 0, sm: "-1.5px" },
      border: {
        xs: `1px solid ${active ? chip.activeBorder : chip.idleBorder}`,
        sm: "none",
      },
      borderBottom: {
        xs: `1px solid ${active ? chip.activeBorder : chip.idleBorder}`,
        sm: `${tabs.indicatorWidth} solid ${
          active ? tabs.activeColor : "transparent"
        }`,
      },
      borderRadius: { xs: chip.radius, sm: 0 },
      bgcolor: { xs: active ? chip.activeBg : chip.idleBg, sm: "transparent" },
      color: {
        xs: active ? chip.activeColor : chip.idleColor,
        sm: active ? tabs.activeTextColor : tabs.inactiveColor,
      },
      fontSize: { xs: chip.fontSize, sm: tablet.tabFontSize, md: tabs.fontSize },
      // The tablet reference bolds the selected label; the desktop screen never
      // did, so `md` puts the single weight back.
      fontWeight: {
        xs: chip.fontWeight,
        sm: active ? tabs.activeWeight : tabs.inactiveWeight,
        md: tabs.inactiveWeight,
      },
      lineHeight: "normal",
      textTransform: "none",
      cursor: "pointer",
      fontFamily: "inherit",
      "&:hover": {
        bgcolor: { xs: active ? chip.activeBg : chip.idleBg, sm: "transparent" },
      },
      // `disableRipple` leaves keyboard focus with no affordance at all, so the
      // tab needs its own indicator (WCAG 2.4.7). Offset keeps it clear of the
      // underline without moving the tab itself.
      "&:focus-visible": {
        outline: `2px solid ${tabs.activeColor}`,
        outlineOffset: "3px",
        borderRadius: "2px",
      },
    }) as SxProps<Theme>,

  /** `status` is the pair of half-width cards, a point smaller on the tablet. */
  cardTitle: (variant: "trends" | "status" = "trends") =>
    ({
      color: cardHeading.titleColor,
      fontSize: {
        xs: phone.card.titleFontSize.std,
        sm:
          variant === "status"
            ? tablet.card.statusTitleFontSize
            : tablet.card.titleFontSize,
        md: cardHeading.titleFontSize,
      },
      fontWeight: cardHeading.titleFontWeight,
      lineHeight: 1.2,
      [PHONE_SM_QUERY]: { fontSize: phone.card.titleFontSize.sm },
    }) as SxProps<Theme>,

  /**
   * The reference draws no strapline under a card heading below `md` — the two
   * filter controls it describes are dropped there too (see `filterRow`).
   */
  cardSubtitle: {
    display: { xs: "none", md: "block" },
    mt: cardHeading.subtitleMarginTop,
    color: cardHeading.subtitleColor,
    fontSize: cardHeading.subtitleFontSize,
    lineHeight: 1.4,
  } as SxProps<Theme>,

  /** Status / date-range controls, which the reference drops below `md`. */
  filterRow: {
    display: { xs: "none", md: "flex" },
  } as SxProps<Theme>,

  /** Same, for the row that also has to fill the card. */
  filterRowFull: {
    display: { xs: "none", md: "flex" },
    width: "100%",
  } as SxProps<Theme>,

  /**
   * The desktop card's drop shadow is passed in rather than read here: it lives
   * on `theme.palette.chart.cardShadow`, and the reference's phone and tablet
   * cards are flat, so the value has to vary by breakpoint.
   */
  card: (shadow: string, variant: "trends" | "status" = "trends") =>
    ({
      p: {
        xs: phone.card.padding.std,
        sm:
          variant === "status"
            ? tablet.card.statusPadding
            : tablet.card.padding,
        md: 3,
      },
      borderRadius: {
        xs: phone.card.radius,
        sm: tablet.card.radius,
        md: "12px",
      },
      border: "1px solid",
      borderColor: { xs: phone.card.border, md: "chart.cardBorder" },
      boxShadow: { xs: "none", md: shadow },
      bgcolor: "background.paper",
      height: "100%",
      [PHONE_SM_QUERY]: { padding: phone.card.padding.sm },
    }) as SxProps<Theme>,

  /** `minHeight` is what the two Status-tab panels reserve for their loader. */
  chartPanel: (minHeight?: number) =>
    ({
      border: "1px solid",
      borderColor: "chart.panelBorder",
      borderRadius: {
        xs: phone.card.panelRadius,
        sm: tablet.card.panelRadius,
        md: "10px",
      },
      p: {
        xs: phone.card.panelPadding.std,
        sm: tablet.card.panelPadding,
        md: 2,
      },
      bgcolor: "chart.canvas",
      overflow: "hidden",
      ...(minHeight === undefined ? {} : { minHeight }),
      [PHONE_SM_QUERY]: { padding: phone.card.panelPadding.sm },
    }) as SxProps<Theme>,

  /** Restores the 16px the inner desktop `Stack` put between tabs and panel. */
  panel: {
    mt: { xs: 0, md: 2 },
  } as SxProps<Theme>,

  /** Progress and Status distribution: stacked on a phone, paired at 834. */
  statusGrid: {
    width: "100%",
    minWidth: 0,
    display: "grid",
    gap: { xs: phone.gridGap, sm: tablet.gridGap, md: 2 },
    gridTemplateColumns: {
      xs: "1fr",
      sm: "repeat(2, minmax(0, 1fr))",
    },
    "& > *": { minWidth: 0 },
    pb: { xs: 0, md: 5 },
  } as SxProps<Theme>,
};

export default insightsStyles;
